import React, { memo, useCallback, useRef } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, func, number, oneOfType, shape, string,
} from 'prop-types'

// Arrow keys move between tabs per the tablist pattern; Home/End jump to the ends.
const STEP_BY_KEY = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
}

function LibraryTabs({ tabs, activeTab, onSelect }) {
  const tabRefs = useRef({})

  const handleKeyDown = useCallback((event) => {
    const step = STEP_BY_KEY[event.key]
    const isEdgeKey = event.key === 'Home' || event.key === 'End'
    if (!step && !isEdgeKey) {
      return
    }

    event.preventDefault()
    const currentIndex = tabs.findIndex(({ id }) => id === activeTab)
    let nextIndex

    if (isEdgeKey) {
      nextIndex = event.key === 'Home' ? 0 : tabs.length - 1
    } else {
      nextIndex = (currentIndex + step + tabs.length) % tabs.length
    }

    const nextTab = tabs[nextIndex]
    if (!nextTab) {
      return
    }

    onSelect(nextTab.id)
    tabRefs.current[nextTab.id]?.focus()
  }, [tabs, activeTab, onSelect])

  return (
    <div className="library-tabs" role="tablist">
      {tabs.map(({ id, label, count }) => {
        const isActive = id === activeTab

        return (
          <button
            key={id}
            ref={(node) => { tabRefs.current[id] = node }}
            type="button"
            role="tab"
            id={`library-tab-${id}`}
            aria-selected={isActive}
            aria-controls={`library-tabpanel-${id}`}
            // Only the active tab is in the tab order; arrows move between them.
            tabIndex={isActive ? 0 : -1}
            className={classnames(
              'library-tabs__tab cgt-body-base-semibold',
              isActive && 'library-tabs__tab--active',
            )}
            onClick={() => onSelect(id)}
            onKeyDown={handleKeyDown}
          >
            <span className="library-tabs__label">{label}</span>
            {/* An empty library shows no badge at all rather than a zero. */}
            {count > 0 && <span className="library-tabs__count cgt-caption">{count}</span>}
          </button>
        )
      })}
    </div>
  )
}

LibraryTabs.propTypes = {
  tabs: arrayOf(shape({
    id: string.isRequired,
    label: string.isRequired,
    count: oneOfType([string, number]),
  })).isRequired,
  activeTab: string.isRequired,
  onSelect: func.isRequired,
}

export default memo(LibraryTabs)
